import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { GoonAPIClient, Post, UserProfile } from '../client/ApiClient';
import { API_ENDPOINT } from '../constants';
import { prepareImageAsWebP } from '../utils/webp';

@customElement('me-page')
export class MePage extends LitElement {
  createRenderRoot() { return this; }

  @state() displayName = '';
  @state() userProfile: UserProfile | null = null;
  @state() bio = '';
  @state() avatarFile = null;
  @state() pinnedPosts: Post[] = [];
  @state() posts: Post[] = [];
  @state() page = 1;
  @state() loading = false;
  @state() done = false;
  @state() error = '';

  @state() profiles: UserProfile[] = [];

  async firstUpdated() {
    await this.loadProfile();
    await this.loadPinned();
    await this.loadPosts();
    await this.loadProfiles();

    const sentinel = this.renderRoot.querySelector('#feed-sentinel');
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) this.loadPosts();
    }, { rootMargin: '30px' });
    observer.observe(sentinel!);
  }

  async loadProfile() {
    try {
      const user = await new GoonAPIClient(API_ENDPOINT).getCurrentUserProfile();
      this.displayName = user.display_name;
      this.bio = user.bio;
      this.userProfile = user;
    } catch (err) {
      console.error('Failed to load profile', err);
    }
  }

  async loadPinned() {
    try {
      if (this.userProfile) {
        const client = new GoonAPIClient(API_ENDPOINT);
        const posts = await client.getUserProfilePinnedPosts(this.userProfile.slug);
        this.pinnedPosts = posts;
      }
    } catch (err) {
      console.error('Failed to load pinned posts', err);
    }
  }

  async loadPosts() {
    if (this.loading || this.done) return;
    this.loading = true;
    try {
      if (this.userProfile) {
        const client = new GoonAPIClient(API_ENDPOINT);
        const res = await client.getUserProfilePosts(this.userProfile.slug, this.page);
        if (!res.length) this.done = true;
        this.posts = [...this.posts, ...res];
        this.page++;
      }
    } catch (err) {
      console.error('Failed to load posts', err);
    } finally {
      this.loading = false;
    }
  }

  handleFileUpload(e: Event) {
    const input = e.target as HTMLInputElement;
    this.avatarFile = input.files?.[0] || null;
  }

  async handleSaveProfile() {
    try {
      const client = new GoonAPIClient(API_ENDPOINT);
      let imageUrl = undefined;

      if (this.avatarFile) {
        const webpBlob = await prepareImageAsWebP(this.avatarFile, 256, 256);
        const webpFile = new File([webpBlob], 'avatar.webp', { type: 'image/webp' });
        const mediaUrl = await client.uploadMediaAndWait(webpFile, 'persona', 'image/webp');
        imageUrl = mediaUrl;
      }

      await client.updateUserProfile({
        display_name: this.displayName,
        bio: this.bio,
        avatar_url: imageUrl,
      });

      location.reload();
    } catch (err) {
      this.error = 'Failed to save profile';
      console.error(err);
    }
  }

  async loadProfiles() {
    try {
      const client = new GoonAPIClient(API_ENDPOINT);
      const res = await client.getMyProfiles();
      this.profiles = res.profiles;
    } catch (err) {
      console.error('Failed to load profiles', err);
    }
  }

  async switchProfile(profileId: string) {
    try {
      const client = new GoonAPIClient(API_ENDPOINT);
      await client.switchProfile(profileId);
      location.reload();
    } catch (err) {
      console.error('Failed to switch profile', err);
    }
  }

  render() {
    return html`
    <!-- Header -->
    <auth-provider>
      <app-header></app-header>
    </auth-provider>

    <div class="flex flex-col lg:flex-row gap-6 px-4 sm:px-6 py-6 mx-auto w-full">

      <div class="w-full lg:w-4/6">

        <my-profiles></my-profiles>

        <auth-provider>
          <!-- Pinned Posts -->
          <div class="card bg-base-300 p-4 mb-6 hidden">
            <h2 class="text-lg font-bold mb-4">Pinned Posts</h2>
            ${this.pinnedPosts.length === 0 ? html`
              <div class="text-center">No pinned posts. <button class="btn btn-sm btn-primary mt-2">Go Pin One</button></div>
            ` : this.pinnedPosts.map(post => html`<post-card
              .post=${post}
              .creator=${this.userProfile}
            ></post-card>`)}
          </div>

          <!-- User's Posts -->
          <div class="flex flex-col gap-4">
            ${this.posts.map(post => html`<post-card
              .post=${post}
              .creator=${this.userProfile}
            ></post-card>`)}
            <div id="feed-sentinel" class="h-12"></div>
          </div>
        <auth-provider>
      </div>

      <!-- Chat Sidebar -->
      <aside class="w-full lg:w-2/6">
        <div class="sticky top-20">
          <realtime-chat></realtime-chat>
        </div>
      </aside>

    </div>
  `;
  }

}

